/**
 * useGrowthBook - Reactive Vue composable for GrowthBook feature flags
 *
 * This composable provides reactive access to GrowthBook feature flags
 * using a module-level singleton pattern. It wraps GrowthBookService
 * and triggers Vue reactivity when features update.
 *
 * Key principles:
 * - Module-level singleton refs (shared across all component instances)
 * - Version counter pattern to trigger reactivity on feature updates
 * - Computed refs for feature evaluation (always reflect current state)
 * - Non-blocking initialization (app doesn't wait for features to load)
 */

import { ref, computed, readonly } from "vue";
import type { Ref, ComputedRef } from "vue";
import { GrowthBookService } from "@/services/GrowthBookService";
import type { ExperimentAssignment } from "@/services/GrowthBookService";

// =============================================================================
// Module-level singleton state
// =============================================================================

/**
 * Whether GrowthBook is ready (initialized and features loaded)
 */
const isReady = ref<boolean>(false);

/**
 * Features version counter - incremented whenever features update
 * This triggers reactivity in all computed refs that depend on it
 */
const featuresVersion = ref<number>(0);

/**
 * Whether init has been called (prevents multiple initializations)
 */
let isInitCalled = false;

// =============================================================================
// Main Composable
// =============================================================================

export function useGrowthBook() {
  /**
   * Initialize GrowthBook with user attributes
   * Sets up feature update subscription to trigger Vue reactivity
   *
   * @param attributes - User targeting attributes (userId, licenseId, etc.)
   */
  async function initGrowthBook(
    attributes: Record<string, any> = {},
    onExperimentAssignment?: (assignment: ExperimentAssignment) => void,
  ): Promise<void> {
    if (isInitCalled) {
      console.warn("[useGrowthBook] Init already called, skipping duplicate initialization");
      return;
    }

    isInitCalled = true;

    const service = GrowthBookService.getInstance();

    try {
      // Initialize the service (non-blocking - app continues while features load)
      await service.init(attributes, onExperimentAssignment);

      // Mark as ready and trigger initial reactivity
      isReady.value = true;
      featuresVersion.value++;

      // Set up subscription to feature updates
      // GrowthBook uses SSE streaming - features can update in real-time
      // We listen to the internal GrowthBook instance's feature refresh events
      const gb = (service as any).gb; // Access private gb instance

      if (gb) {
        // Subscribe to feature updates using GrowthBook's refresh callback
        // This fires whenever features are updated via SSE
        gb.setRenderer(() => {
          // Increment version counter to trigger reactivity in all computed refs
          featuresVersion.value++;
        });
      }
    } catch (error) {
      console.error("[useGrowthBook] Initialization failed", error);
      // Even on error, mark as ready (with default values) so app continues
      isReady.value = true;
    }
  }

  /**
   * Check if a feature flag is enabled
   * Returns a reactive computed ref that updates when features change
   *
   * @param key - Feature flag key
   * @returns Computed ref that is true if feature is on, false otherwise
   *
   * @example
   * const showNewUI = isOn("new-ui-enabled");
   * // In template: <div v-if="showNewUI">...</div>
   */
  function isOn(key: string): ComputedRef<boolean> {
    return computed(() => {
      // Depend on featuresVersion to trigger reactivity
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const _ = featuresVersion.value;

      // Delegate to service for actual evaluation
      return GrowthBookService.getInstance().isOn(key);
    });
  }

  /**
   * Get a feature value with fallback
   * Returns a reactive computed ref that updates when features change
   *
   * @param key - Feature flag key
   * @param fallback - Fallback value if feature is not found
   * @returns Computed ref with feature value or fallback
   *
   * @example
   * const buttonColor = getFeatureValue("button-color", "blue");
   * // In template: <button :style="{ color: buttonColor }">...</button>
   */
  function getFeatureValue<T = any>(key: string, fallback: T): ComputedRef<T> {
    return computed(() => {
      // Depend on featuresVersion to trigger reactivity
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const _ = featuresVersion.value;

      // Delegate to service for actual evaluation
      return GrowthBookService.getInstance().getFeatureValue(key, fallback);
    });
  }

  /**
   * Update user targeting attributes
   * Call this when user context changes (e.g., switching patients)
   *
   * @param attrs - Attributes to merge with existing ones
   */
  function setAttributes(attrs: Record<string, any>): void {
    GrowthBookService.getInstance().setAttributes(attrs);

    // Increment version to trigger reactivity (attributes affect feature evaluation)
    featuresVersion.value++;
  }

  /**
   * Wait for GrowthBook to be ready with timeout
   * Useful for components that need to wait before rendering
   *
   * @param timeoutMs - Timeout in milliseconds (default: 3000)
   * @returns Promise that resolves to true if ready, false if timeout
   *
   * @example
   * const ready = await waitForReady(5000);
   * if (!ready) {
   *   console.warn("Features not loaded, using defaults");
   * }
   */
  async function waitForReady(timeoutMs = 3000): Promise<boolean> {
    return GrowthBookService.getInstance().waitForReady(timeoutMs);
  }

  /**
   * Get all features (for DevPanel inspection)
   * @returns All features from GrowthBook
   */
  function getAllFeatures(): Record<string, any> {
    return GrowthBookService.getInstance().getAllFeatures();
  }

  /**
   * Whether the features payload was actually loaded.
   * isReady can flip true even when the fetch failed (empty payload, every
   * flag at its default) — use this to qualify experiment assignment events.
   */
  function hasLoadedFeatures(): boolean {
    return GrowthBookService.getInstance().hasLoadedFeatures();
  }

  /**
   * Force specific feature values (for testing/DevPanel)
   * @param map - Map of feature keys to values
   */
  function setForcedFeatures(map: Record<string, any>): void {
    GrowthBookService.getInstance().setForcedFeatures(map);

    // Increment version to trigger reactivity
    featuresVersion.value++;
  }

  /**
   * Get current user attributes
   * @returns Current attributes
   */
  function getAttributes(): Record<string, any> {
    return GrowthBookService.getInstance().getAttributes();
  }

  // ---------------------------------------------------------------------------
  // Return Public API
  // ---------------------------------------------------------------------------

  return {
    // Reactive state (readonly to prevent external mutation)
    isReady: readonly(isReady),
    featuresVersion: readonly(featuresVersion),

    // Core functions
    initGrowthBook,
    isOn,
    getFeatureValue,
    setAttributes,
    waitForReady,
    hasLoadedFeatures,

    // DevPanel utilities
    getAllFeatures,
    setForcedFeatures,
    getAttributes,
  };
}
